import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DataTable } from "../../components/DataTable";
import { ConfirmDialog, Dialog } from "../../components/Dialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  TextArea,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { ApiError, api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatBytes, formatDate, formatDateTime } from "../../lib/format";
import type { Page } from "../../lib/types";
import type { UploadInfo } from "../../lib/uploader";
import type { Delivery } from "../files/FilesPage";
import { FileUploader, type AttachedFile } from "../orders/FileUploader";

const statusTone = {
  draft: "warn",
  released: "ok",
  expired: "neutral",
  cancelled: "neutral",
} as const;
const statusLabel = {
  draft: "not sent",
  released: "sent",
  expired: "expired",
  cancelled: "cancelled",
} as const;
const linkBtn =
  "inline-flex min-h-11 items-center rounded-md bg-brand-600 px-4 font-semibold text-white hover:bg-brand-700";

export function StaffFilesPage() {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ["staff-deliveries", page],
    queryFn: () => api.get<Page<Delivery>>(`/staff/deliveries?page=${page}`),
  });
  return (
    <div>
      <PageHeader
        title="Files FROM Bill's"
        description="Email scans and finished files to anyone. They get a download link that works for 45 days."
        actions={
          <Link to="/staff/files/new" className={linkBtn}>
            New package
          </Link>
        }
      />
      <DataTable
        caption="File packages"
        loading={q.isLoading}
        rows={q.data?.items}
        rowKey={(d) => d.id}
        page={page}
        pageSize={25}
        total={q.data?.total}
        onPage={setPage}
        emptyTitle="No packages yet"
        columns={[
          {
            key: "title",
            header: "Package",
            render: (d) => (
              <Link className="font-semibold text-brand-700 underline" to={`/staff/files/${d.id}`}>
                {d.title}
              </Link>
            ),
          },
          { key: "to", header: "For", render: (d) => d.recipient },
          {
            key: "status",
            header: "Status",
            render: (d) => <Badge tone={statusTone[d.status]}>{statusLabel[d.status]}</Badge>,
          },
          {
            key: "dl",
            header: "Downloaded",
            render: (d) =>
              `${d.downloaded_count} of ${d.files.filter((f) => f.available || d.status !== "draft").length}`,
          },
          { key: "expires", header: "Expires", render: (d) => formatDate(d.expires_at, tz) },
        ]}
      />
    </div>
  );
}

/** Comma, semicolon or whitespace separated list of addresses. */
function splitEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

interface EmailFields {
  recipient_name: string;
  recipient_email: string;
  cc: string;
  title: string;
  email_subject: string;
  message: string;
}

function emailBody(v: EmailFields) {
  return {
    title: v.title,
    recipient_name: v.recipient_name,
    recipient_email: v.recipient_email,
    cc_emails: splitEmails(v.cc),
    email_subject: v.email_subject || null,
    message: v.message || null,
  };
}

function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiError
    ? Object.fromEntries(err.fields.map((f) => [f.field, f.message]))
    : {};
}

/** Search the Customers list and fill in the recipient's name and email. */
function CustomerLookup({ onPick }: { onPick: (c: { name: string; email: string }) => void }) {
  const [q, setQ] = useState("");
  const results = useQuery({
    queryKey: ["customer-lookup", q],
    queryFn: () =>
      api.get<Page<{ id: string; name: string; email: string }>>(
        `/staff/customers?${new URLSearchParams({ q, page_size: "8" })}`,
      ),
    enabled: q.trim().length >= 2,
  });
  return (
    <div>
      <TextField
        label="Look up a customer"
        placeholder="Type a customer's name or email"
        hint="Or type the name and email address below."
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {q.trim().length >= 2 && results.data && (
        <ul
          className="mt-2 max-h-56 divide-y divide-line overflow-y-auto rounded-md border border-line bg-white"
          aria-label="Matching customers"
        >
          {results.data.items.length === 0 && (
            <li className="p-2 text-sm text-ink-muted">No matching customers.</li>
          )}
          {results.data.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="w-full p-2 text-left hover:bg-brand-50"
                onClick={() => {
                  onPick(c);
                  setQ("");
                }}
              >
                {c.name} <span className="text-sm text-ink-muted">({c.email})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Name, email, CC, package title, subject and body of an emailed package. */
function EmailFieldsForm({
  value,
  onChange,
  errors,
}: {
  value: EmailFields;
  onChange: (v: EmailFields) => void;
  errors: Record<string, string>;
}) {
  const set = (k: keyof EmailFields) => (e: { target: { value: string } }) =>
    onChange({ ...value, [k]: e.target.value });
  const ccError = Object.entries(errors).find(([k]) => k.startsWith("cc_emails"))?.[1];
  return (
    <div className="grid items-start gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <CustomerLookup
          onPick={(c) => onChange({ ...value, recipient_name: c.name, recipient_email: c.email })}
        />
      </div>
      <TextField
        label="Recipient name"
        required
        autoComplete="off"
        value={value.recipient_name}
        error={errors.recipient_name}
        onChange={set("recipient_name")}
      />
      <TextField
        label="Email address"
        type="email"
        required
        autoComplete="off"
        value={value.recipient_email}
        error={errors.recipient_email ?? errors.target}
        onChange={set("recipient_email")}
      />
      <div className="sm:col-span-2">
        <TextField
          label="CC"
          hint="Separate several addresses with commas."
          value={value.cc}
          error={ccError}
          onChange={set("cc")}
        />
      </div>
      <div className="sm:col-span-2">
        <TextField
          label="Package title"
          required
          value={value.title}
          error={errors.title}
          onChange={set("title")}
        />
      </div>
      <div className="sm:col-span-2">
        <TextField
          label="Subject"
          placeholder={`Files from Bill's Blueprint: ${value.title || "package title"}`}
          hint="Leave blank to use the text shown."
          value={value.email_subject}
          error={errors.email_subject}
          onChange={set("email_subject")}
        />
      </div>
      <div className="sm:col-span-2">
        <TextArea
          label="Body"
          rows={6}
          placeholder={`Hello ${value.recipient_name || "name"},\n\nBill's has sent you files: ${value.title || "package title"}.`}
          hint="The list of files, the download button and the expiry date are added below your text."
          value={value.message}
          error={errors.message}
          onChange={set("message")}
        />
      </div>
    </div>
  );
}

export function StaffNewPackagePage() {
  const navigate = useNavigate();
  const [value, setValue] = useState<EmailFields>({
    recipient_name: "",
    recipient_email: "",
    cc: "",
    title: "",
    email_subject: "",
    message: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="New file package"
        description="Next you will add files and preview the email before it is sent."
      />
      <Card>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setErrors({});
            setBusy(true);
            try {
              const d = await api.post<Delivery>("/staff/deliveries", emailBody(value));
              navigate(`/staff/files/${d.id}`);
            } catch (err) {
              setError(errorMessage(err));
              setErrors(fieldErrors(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {error && <Alert tone="error">{error}</Alert>}
          <EmailFieldsForm value={value} onChange={setValue} errors={errors} />
          <Button
            type="submit"
            busy={busy}
            className="self-start"
            disabled={
              !value.title.trim() || !value.recipient_name.trim() || !value.recipient_email.trim()
            }
          >
            Next: add files
          </Button>
        </form>
      </Card>
    </div>
  );
}

interface EmailPreview {
  to: string;
  cc: string[];
  subject: string;
  html: string;
  text: string;
}

/** Shows the exact email (with a placeholder link) and sends it on confirmation. */
function EmailPreviewDialog({
  delivery: d,
  onClose,
  onSent,
}: {
  delivery: Delivery;
  onClose: () => void;
  onSent: (d: Delivery) => void;
}) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const preview = useQuery({
    queryKey: ["delivery-preview", d.id, d.files.length, d.title, d.message, d.email_subject],
    queryFn: () => api.get<EmailPreview>(`/staff/deliveries/${d.id}/email-preview`),
  });
  const p = preview.data;
  return (
    <Dialog
      open
      wide
      title="Email preview"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Go back and edit
          </Button>
          <Button
            busy={busy}
            disabled={!p || d.files.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                onSent(await api.post<Delivery>(`/staff/deliveries/${d.id}/release`));
                notify("Email sent. Bill's also gets a copy.");
                onClose();
              } catch (err) {
                notify(errorMessage(err), "error");
              } finally {
                setBusy(false);
              }
            }}
          >
            Send email
          </Button>
        </>
      }
    >
      {preview.isLoading && <LoadingBlock />}
      {preview.error && <Alert tone="error">{errorMessage(preview.error)}</Alert>}
      {p && (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="font-semibold">To</dt>
            <dd>{p.to}</dd>
            <dt className="font-semibold">CC</dt>
            <dd>{p.cc.length ? p.cc.join(", ") : "None"}</dd>
            <dt className="font-semibold">Subject</dt>
            <dd>{p.subject}</dd>
          </dl>
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={p.html}
            className="h-[55vh] w-full rounded border border-line bg-white"
          />
          <p className="text-sm text-ink-muted">
            The files are not attached. The button opens a secure download page that works until the
            files expire. Bill's gets a copy of this email, and another email when the files are
            first downloaded.
          </p>
        </div>
      )}
    </Dialog>
  );
}

export function StaffPackagePage() {
  const { id } = useParams();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const qc = useQueryClient();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const [confirm, setConfirm] = useState<"release" | "cancel" | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [removeFile, setRemoveFile] = useState<{ id: string; name: string } | null>(null);
  const [reason, setReason] = useState("");
  const q = useQuery({
    queryKey: ["staff-delivery", id],
    queryFn: () => api.get<Delivery>(`/staff/deliveries/${id}`),
    refetchInterval: (query) =>
      (query.state.data?.pending_uploads ?? []).some((p) => p.state === "checking") ? 3000 : false,
  });
  if (q.isLoading || !config) return <LoadingBlock />;
  if (!q.data) return <Alert tone="error">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const draft = d.status === "draft";
  const byEmail = !!d.recipient_email;
  const refresh = () => qc.invalidateQueries({ queryKey: ["staff-delivery", id] });
  const uploading = files.some((f) =>
    ["queued", "uploading", "finishing", "checking"].includes(f.progress.state),
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={d.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone[d.status]}>{statusLabel[d.status]}</Badge> For {d.recipient}
            {d.released_at &&
              ` · sent ${formatDateTime(d.released_at, tz)} · expires ${formatDateTime(d.expires_at, tz)}`}
          </span>
        }
      />
      {byEmail ? (
        <Card>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold">Email</h2>
            {draft && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit email
              </Button>
            )}
          </div>
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
            <dt className="font-semibold">To</dt>
            <dd>{d.recipient}</dd>
            <dt className="font-semibold">CC</dt>
            <dd>{d.cc_emails?.length ? d.cc_emails.join(", ") : "None"}</dd>
            <dt className="font-semibold">Subject</dt>
            <dd>{d.email_subject}</dd>
            <dt className="font-semibold">Body</dt>
            <dd className="whitespace-pre-wrap">{d.message || "(standard greeting)"}</dd>
          </dl>
        </Card>
      ) : (
        d.message && <p className="whitespace-pre-wrap">{d.message}</p>
      )}
      {draft && (
        <Card>
          <h2 className="mb-3 text-lg font-bold">Add files</h2>
          <FileUploader
            maxBytes={config.upload_max_bytes}
            chunkBytes={config.upload_chunk_bytes}
            files={files}
            onChange={(updater) => {
              setFiles((prev) => {
                const next = updater(prev);
                if (
                  next.some(
                    (f, i) => f.progress.state === "ready" && prev[i]?.progress.state !== "ready",
                  )
                )
                  refresh();
                return next;
              });
            }}
            hint="PDF, TIFF, PNG, JPEG, DWG, DXF, DWF, PLT or ZIP."
            accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg,.dwg,.dxf,.dwf,.dwfx,.plt,.hpgl,.hpg,.zip"
            initiate={(file) =>
              api.post<UploadInfo>(`/staff/deliveries/${d.id}/uploads`, {
                filename: file.name,
                size: file.size,
                scope: "delivery",
              })
            }
          />
        </Card>
      )}
      <Card>
        <h2 className="mb-3 text-lg font-bold">Files in this package</h2>
        {d.files.length === 0 ? (
          <p className="text-ink-muted">No files yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {d.files.map((f) => (
              <li
                key={f.id}
                className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  {f.name}{" "}
                  <span className="text-sm text-ink-muted">({formatBytes(f.size_bytes)})</span>
                  {!f.available && d.status !== "draft" && <Badge>Removed</Badge>}
                </span>
                <span className="flex items-center gap-3 text-sm">
                  {f.downloaded ? (
                    <Badge tone="ok">Downloaded {formatDate(f.first_downloaded_at, tz)}</Badge>
                  ) : d.status === "released" ? (
                    <Badge tone="warn">Not downloaded</Badge>
                  ) : null}
                  {typeof f.download_count === "number" && d.status !== "draft" && (
                    <span>{f.download_count} downloads</span>
                  )}
                  {draft && (
                    <Button
                      variant="ghost"
                      onClick={async () => {
                        await api.del(`/staff/deliveries/${d.id}/files/${f.id}`);
                        refresh();
                      }}
                    >
                      Remove
                    </Button>
                  )}
                  {d.status === "released" && f.available && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setReason("");
                        setRemoveFile({ id: f.id, name: f.name });
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {draft && (
        <div className="flex flex-wrap gap-3">
          <Button
            disabled={d.files.length === 0 || uploading}
            onClick={() => (byEmail ? setPreviewing(true) : setConfirm("release"))}
          >
            {byEmail ? "Preview and send email" : "Release to customer"}
          </Button>
          <Button variant="secondary" onClick={() => setConfirm("cancel")}>
            Cancel package
          </Button>
          {uploading && (
            <span className="self-center text-sm text-ink-muted">
              Wait for uploads and security checks to finish.
            </span>
          )}
        </div>
      )}
      {d.status === "released" && (
        <Card>
          <h2 className="text-lg font-bold">Sent by mistake?</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Send the files to the right person (the old download link stops working at once), remove
            a single file above, or withdraw the whole package.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {byEmail && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Send to a different person
              </Button>
            )}
            <Button
              variant="danger"
              onClick={() => {
                setReason("");
                setWithdrawing(true);
              }}
            >
              Withdraw package
            </Button>
          </div>
        </Card>
      )}
      {editing && (
        <PackageEditDialog delivery={d} onClose={() => setEditing(false)} onSaved={refresh} />
      )}
      {previewing && (
        <EmailPreviewDialog
          delivery={d}
          onClose={() => setPreviewing(false)}
          onSent={(sent) => qc.setQueryData(["staff-delivery", id], sent)}
        />
      )}
      <Dialog
        open={withdrawing}
        title="Withdraw this package?"
        onClose={() => setWithdrawing(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setWithdrawing(false)}>
              Go back
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              onClick={async () => {
                setWithdrawing(false);
                try {
                  await api.post(`/staff/deliveries/${d.id}/withdraw`, { reason });
                  notify(
                    "Package withdrawn. The files were deleted and the download link no longer works.",
                  );
                  await refresh();
                } catch (err) {
                  notify(errorMessage(err), "error");
                }
              }}
            >
              Withdraw and delete files
            </Button>
          </>
        }
      >
        <p className="mb-3">
          {d.recipient} will no longer be able to download this package, and its files will be
          deleted from the server.
        </p>
        <TextField
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Dialog>
      <Dialog
        open={!!removeFile}
        title={`Remove ${removeFile?.name ?? "file"}?`}
        onClose={() => setRemoveFile(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveFile(null)}>
              Go back
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const f = removeFile!;
                setRemoveFile(null);
                try {
                  await api.del(
                    `/staff/deliveries/${d.id}/files/${f.id}?${new URLSearchParams({ reason })}`,
                  );
                  notify("File removed. It can no longer be downloaded.");
                  await refresh();
                } catch (err) {
                  notify(errorMessage(err), "error");
                }
              }}
            >
              Remove file
            </Button>
          </>
        }
      >
        <p className="mb-3">
          The file can no longer be downloaded and it is deleted from the server.
        </p>
        <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Dialog>
      <ConfirmDialog
        open={confirm === "release"}
        title="Release these files?"
        body={
          <p>
            {d.recipient} will get an email and can download {d.files.length} file
            {d.files.length === 1 ? "" : "s"} for 45 days. If you send them to the wrong place you
            can withdraw them.
          </p>
        }
        confirmLabel="Release"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          setBusy(true);
          try {
            qc.setQueryData(
              ["staff-delivery", id],
              await api.post<Delivery>(`/staff/deliveries/${d.id}/release`),
            );
            notify("Files released. The customer was emailed.");
          } catch (err) {
            notify(errorMessage(err), "error");
          } finally {
            setBusy(false);
            setConfirm(null);
          }
        }}
      />
      <ConfirmDialog
        open={confirm === "cancel"}
        title="Cancel this package?"
        body="The uploaded files will be deleted. Nobody is emailed."
        confirmLabel="Cancel package"
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          await api
            .del(`/staff/deliveries/${d.id}`)
            .catch((err) => notify(errorMessage(err), "error"));
          navigate("/staff/files");
        }}
      />
    </div>
  );
}

function PackageEditDialog({
  delivery: d,
  onClose,
  onSaved,
}: {
  delivery: Delivery;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { notify } = useToast();
  const [value, setValue] = useState<EmailFields>({
    recipient_name: d.recipient_name ?? "",
    recipient_email: d.recipient_email ?? "",
    cc: (d.cc_emails ?? []).join(", "),
    title: d.title,
    email_subject: d.email_subject ?? "",
    message: d.message ?? "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const released = d.status === "released";
  const newAddress =
    value.recipient_email.trim().toLowerCase() !== (d.recipient_email ?? "").toLowerCase();
  return (
    <Dialog
      open
      wide
      title={released ? "Send to a different person" : "Edit email"}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            busy={busy}
            disabled={!value.title.trim() || !value.recipient_email.trim()}
            onClick={async () => {
              setBusy(true);
              setErrors({});
              try {
                await api.patch(`/staff/deliveries/${d.id}`, emailBody(value));
                notify(
                  released && newAddress
                    ? `Sent to ${value.recipient_email}. The old download link no longer works.`
                    : "Package updated.",
                );
                await onSaved();
                onClose();
              } catch (err) {
                setErrors(fieldErrors(err));
                notify(errorMessage(err), "error");
              } finally {
                setBusy(false);
              }
            }}
          >
            {released && newAddress ? "Save and send to new recipient" : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <EmailFieldsForm value={value} onChange={setValue} errors={errors} />
        {released && newAddress && (
          <Alert tone="warning">
            The files will be emailed to {value.recipient_email || "the new address"} with a new
            download link and a fresh 45-day window. The old link stops working immediately.
          </Alert>
        )}
      </div>
    </Dialog>
  );
}
